import axios from 'axios';

/**
 * TAS base URL — accessed on the same Vista domain via CloudFront behavior on /tas/*.
 * In qa12: https://qa12-vista.therigy.net/tas/1.0.0
 * Locally (dev): configured via REACT_APP_TAS_URL env var, falls back to '/tas/1.0.0'
 */
const TAS_BASE_URL = process.env.REACT_APP_TAS_URL || '/tas/1.0.0';

/**
 * Dedicated axios instance for TAS requests.
 * - withCredentials ensures the httpOnly refresh_token cookie is sent.
 * - No Authorization header is injected (the app-wide interceptor should skip TAS calls).
 */
const tasAxios = axios.create({
    baseURL: TAS_BASE_URL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

/**
 * Bootstraps the httpOnly refresh_token cookie on the Vista domain.
 *
 * After SAML login, the IDP posts the assertion to the STM host, so the
 * refresh_token cookie is scoped to that host and invisible to Vista.
 * This function calls GET /refresh with the short-lived sso_jwt as a
 * Bearer token, causing TAS to issue a new refresh_token cookie on the
 * current (Vista) domain.
 *
 * @param {string} ssoJwt - The sso_jwt value read from the cookie.
 * @returns {Promise<object>} The TAS response (user profile data).
 */
export async function bootstrapRefreshCookie(ssoJwt) {
    const response = await tasAxios.get('/refresh', {
        headers: { Authorization: `Bearer ${ssoJwt}` },
    });
    return response.data;
}

/**
 * Calls TAS POST /refresh to rotate the httpOnly refresh_token cookie
 * and receive a fresh access token (JWT).
 *
 * TAS reads the refresh_token cookie, validates & rotates it, sets a new
 * refresh_token cookie in the response, and returns { accessToken }.
 *
 * Deduplication is handled by TokenManagerService.refreshAccessToken()
 * (navigator.locks + singleton promise). This function is intentionally
 * a plain HTTP call with no internal dedup layer.
 *
 * @returns {Promise<{ accessToken: string }>}
 */
export function refreshAccessToken() {
    return tasAxios.post('/refresh').then((response) => response.data);
}

/**
 * Calls TAS POST /revoke to invalidate the current refresh token.
 * Used on explicit logout.
 *
 * @returns {Promise<void>}
 */
export async function revokeRefreshToken() {
    try {
        await tasAxios.post('/revoke');
    } catch (_err) {
        // Best-effort; don't block logout if revoke fails
    }
}

export default {
    bootstrapRefreshCookie,
    refreshAccessToken,
    revokeRefreshToken,
};

